'use client'

/**
 * The alkanes market chart, on the shared TradingView terminal.
 *
 * This is a renderer swap and deliberately nothing more. Every decision about
 * *what* the chart shows stays exactly where it was:
 *
 *   * the USD series when the all-pools index covers this asset, the pool's
 *     own reserve ratio when it does not;
 *   * the reciprocal when the market's base is the pool's token1, with high
 *     and low swapping roles because 1/x mirrors the bar;
 *   * frBTC converted at spot only when a spot price is actually on hand;
 *   * market cap = price x today's supply, the convention DEX terminals use,
 *     stated in the heading rather than hidden.
 *
 * All of that is derived here, from the store, and handed to `TvChart` as
 * bars. The terminal does not fetch for this page — it already has a
 * subscription, and a second fetch would both duplicate the request and
 * re-derive semantics this page owns. What the swap buys is the same drawing
 * tools, resolutions, crosshair and theming as every other chart on the site,
 * plus a better home for the two overlays:
 *
 *   * **My swaps** move from lightweight-charts markers to TradingView
 *     execution shapes. The old markers were snapped to the nearest bar and
 *     disappeared at resolutions where that bar did not exist; an execution
 *     carries its own timestamp and stays put across every zoom.
 *   * **Theses** move from hand-positioned absolute divs, re-measured on every
 *     pan, to datafeed marks the library positions itself.
 */

import React, { FC } from 'react'
import { cn } from '@/lib/utils'
import TvChart from '@/components/tv-chart'
import type { ChartExecution } from '@/components/tv-chart'
import type { OhlcvBar } from '@/components/tv-chart/datafeed'
import { useAlkanes } from '../lib/store'
import { pointPrice } from '../lib/amm'
import { chartHeading } from '../lib/chart-heading'
import useTokenChart from '../lib/useTokenChart'
import type { ChartRange } from '../lib/usePoolChart'
import type { TwetchPost, TwetchUser } from '../lib/twetch-api'
import type { Denom } from '@/components/tv-chart/symbol'

type ThesisMarker = { post: TwetchPost; user?: TwetchUser }
const EMPTY_THESES: ThesisMarker[] = []
const EMPTY_TRADES: { ts: number; side: 'buy' | 'sell' }[] = []

/** Longest thesis tooltip before it is cut — a mark points at a post, it is
 * not a place to read one. */
const MARK_TEXT_LIMIT = 280

/**
 * Which range to fetch for a chosen resolution.
 *
 * The page's own subscription fetches `range: 'all'`, which the feed serves as
 * DAILY bars. That is the right series for a zoomed-out view and the wrong one
 * for any intraday resolution: resampling daily bars into 5-minute buckets
 * cannot invent the minutes, so every sub-daily setting drew the same picture
 * and changing the candle width appeared to do nothing.
 *
 * Each range carries its own bar width upstream — roughly 10m for a day, an
 * hour for a week, four hours for a month, a day for everything. So the
 * resolution the viewer picks selects the range whose native width sits closest
 * below it, and the datafeed aggregates up from there.
 */
const RANGE_FOR_RESOLUTION: Record<string, ChartRange> = {
	'5': '1d',
	'15': '1d',
	'60': '7d',
	'240': '30d',
	'720': 'all',
	'1D': 'all',
	D: 'all',
	'1W': 'all',
	W: 'all'
}

/**
 * Opening resolution — daily, which maps to the full history.
 *
 * The lightweight-charts version opened on `range: 'all'`, and that matters for
 * more than the price: theses are hung on the plot at the time they were
 * posted, so a narrower opening window silently hides every thesis older than
 * it. Opening on an hourly/7-day view made a chart whose "Thesis (1)" counter
 * said there was one and whose plot showed none.
 *
 * Finer resolutions remain one click away and narrow the range with them.
 */
const DEFAULT_RESOLUTION = '1D'

/** Hoisted so these are not fresh values on every render; see
 * `tv-collection-chart` for why that matters to the widget. */
const ALKANE_DENOMS: Denom[] = ['usd', 'mcap']
const ALKANE_DENOMS_PRICE_ONLY: Denom[] = []
const ALKANE_DENOM_LABELS: Partial<Record<Denom, string>> = { usd: 'Price', mcap: 'Mcap' }

const TvMarketChart: FC<{
	/// The viewer's own trades in the open asset, for the "My swaps" overlay:
	/// seconds-precision timestamps plus which side of the asset each was.
	/// Real trades only — the caller builds these from broadcast records and
	/// chain-decoded history, never from anything invented.
	myTrades?: { ts: number; side: 'buy' | 'sell' }[]
	theses?: ThesisMarker[]
	className?: string
}> = ({ myTrades = EMPTY_TRADES, theses = EMPTY_THESES, className }) => {
	const asset = useAlkanes((s) => s.asset)
	const market = useAlkanes((s) => s.market)
	const btcUsd = useAlkanes((s) => s.btcUsd)
	const usdData = useAlkanes((s) => s.tokenChart)
	const poolData = useAlkanes((s) => s.poolChart)
	const usdCovered = useAlkanes((s) => s.usdCovered)
	const isLoading = useAlkanes((s) => s.chartLoading)
	const loadFailed = useAlkanes((s) => s.chartError)
	const retryChart = useAlkanes((s) => s.retryChart)

	// The y-axis reads in MARKET CAP by default — the number traders compare
	// tokens by — with price one tap away. Only a dollar series can be scaled
	// into a dollar cap, and frBTC (a 1:1 BTC wrap) has no cap of its own.
	const [axis, setAxis] = React.useState<'mcap' | 'price'>('mcap')
	const [resolution, setResolution] = React.useState(DEFAULT_RESOLUTION)

	// Fetch the range that matches the chosen resolution. The store's 'all'
	// series stays the fallback — it is already loaded, so the chart has
	// something to draw while a narrower range is in flight, and it stays correct
	// for the daily and weekly resolutions that ask for 'all' anyway.
	const range = RANGE_FOR_RESOLUTION[resolution] ?? 'all'
	const { data: rangedUsd } = useTokenChart(usdCovered ? asset?.id ?? null : null, range)
	const usdSeries = usdCovered || (market?.quote.id === '32:0' && btcUsd !== null)
	const supply =
		usdSeries &&
		usdData?.source !== 'hyperliquid' &&
		asset?.market_cap_usd &&
		asset.price_usd &&
		asset.market_cap_usd > 0 &&
		asset.price_usd > 0
			? asset.market_cap_usd / asset.price_usd
			: null
	const showCap = axis === 'mcap' && supply !== null
	const scale = showCap ? supply : 1

	const bars = React.useMemo<OhlcvBar[]>(() => {
		const bar = (
			time: number,
			open: number,
			high: number,
			low: number,
			close: number,
			// Volume is reported in the quote currency by the feed and is NOT
			// scaled into market cap: a cap is a price times supply, while
			// volume is an amount already traded. Multiplying it by supply
			// would inflate it by four or five orders of magnitude.
			volume = 0
		): OhlcvBar => ({
			time,
			open,
			high,
			low,
			close,
			median: close,
			volume,
			trades: 0,
			synthetic: false
		})

		// Prefer the range-matched series once it arrives; fall back to the
		// store's 'all' series so the chart is never blank mid-switch.
		const usd =
			rangedUsd?.currency === 'usd' && rangedUsd.candles?.length ? rangedUsd : usdData

		if (usdCovered && usd?.candles) {
			return usd.candles
				.filter((c) => c.open > 0 && c.high > 0 && c.low > 0 && c.close > 0)
				.map((c) =>
					bar(
						c.ts,
						c.open * scale,
						c.high * scale,
						c.low * scale,
						c.close * scale,
						c.volume
					)
				)
		}

		if (market && poolData?.candles?.length) {
			// Pool candles carry OHLC of reserve1/reserve0; when the market's
			// base is token1 the price is the reciprocal — 1/x mirrors the bar,
			// so high and low swap roles. Convert frBTC->USD at spot when we can.
			const invert = market.base.id === market.pool.token1.id
			const fx = (market.quote.id === '32:0' && btcUsd !== null ? btcUsd : 1) * scale
			return poolData.candles
				.filter((c) => c.open > 0 && c.high > 0 && c.low > 0 && c.close > 0)
				.map((c) =>
					bar(
						c.ts,
						(invert ? 1 / c.open : c.open) * fx,
						(invert ? 1 / c.low : c.high) * fx,
						(invert ? 1 / c.high : c.low) * fx,
						(invert ? 1 / c.close : c.close) * fx
					)
				)
		}

		// Legacy pool responses that predate the candle field only carry
		// points. A point is one price, so the bar is flat — drawing a body
		// around a single number would be inventing an open and a close.
		if (market && poolData?.points?.length) {
			const fx = (market.quote.id === '32:0' && btcUsd !== null ? btcUsd : 1) * scale
			return poolData.points
				.map((p) => {
					const v = pointPrice(market, p.reserve0, p.reserve1) * fx
					return bar(p.ts, v, v, v, v)
				})
				.filter((b) => b.close > 0)
		}

		return []
	}, [usdCovered, usdData, rangedUsd, market, poolData, btcUsd, scale])

	const hasData = bars.length > 0

	// Suppressed while the pool path is still backfilling — a stat over a
	// partial series is a lie.
	const building = !usdCovered && poolData?.building === true


	const executions = React.useMemo<ChartExecution[]>(
		() =>
			myTrades.map((t) => ({
				ts: t.ts,
				side: t.side,
				tooltip: t.side === 'buy' ? 'You bought' : 'You sold'
			})),
		[myTrades]
	)



	// frBTC's dollar series IS BTC/USD (a 1:1 wrap, served from Hyperliquid).
	// The chart's own legend would only say "frBTC", which reads as if an
	// alkanes feed priced it, so the peg is stated above the plot — the same
	// claim the lightweight-charts header made before the port.
	const peg =
		usdCovered && usdData?.source === 'hyperliquid'
			? chartHeading({ title: asset?.name, denomination: 'USD', source: usdData.source, usdCovered })
			: null

	return (
		<section
			aria-label="Price chart"
			data-testid="alkanes-chart"
			className={cn('flex h-full w-full flex-col', className)}
		>
			{peg && (
				<h2 className="px-1 pb-1 text-xs font-semibold text-muted-foreground">
					{peg.title}
					<span className="ml-1.5 whitespace-nowrap font-normal">{peg.note}</span>
				</h2>
			)}
			{building && (
				<p className="px-1 pb-1 text-xs text-amber-400">
					Building price history — this pool is being backfilled now.
				</p>
			)}

			{loadFailed && !hasData ? (
				<div className="flex h-full min-h-[300px] flex-col items-center justify-center gap-2 text-sm text-white/50">
					<span>Couldn&apos;t load the chart.</span>
					<button
						type="button"
						onClick={() => retryChart?.()}
						className="rounded bg-white/10 px-3 py-1 text-xs text-white hover:bg-white/20"
					>
						Retry
					</button>
				</div>
			) : !hasData ? (
				<div className="flex h-full min-h-[300px] items-center justify-center text-sm text-white/50">
					{isLoading ? 'Loading…' : 'No price history yet.'}
				</div>
			) : (
				<TvChart
					kind="alkane"
					id={asset?.id ?? 'unknown'}
					title={asset?.name || asset?.id}
					// The page owns this series; see the module docs.
					bars={bars}
					// Only offer the cap axis when a supply is actually on hand
					// — frBTC is a 1:1 BTC wrap and has no cap of its own, and
					// a switcher that produces the same chart either way is a
					// lie about there being a choice.
					denoms={supply !== null ? ALKANE_DENOMS : ALKANE_DENOMS_PRICE_ONLY}
					denomLabels={ALKANE_DENOM_LABELS}
					denom={showCap ? 'mcap' : 'usd'}
					onDenomChange={(d) => setAxis(d === 'mcap' ? 'mcap' : 'price')}
					resolution={resolution}
					onResolutionChange={setResolution}
					executions={executions}
					// Faces only — no `getMarks`.
					//
					// TradingView's own marks draw a letter in a circle at the
					// same timestamps the faces use, so supplying both put two
					// markers on the plot for every thesis: a purple 'T' and an
					// avatar, near each other but not aligned, for the same post.
					// The avatars are the ones worth keeping, because a thesis is
					// a person and a letter does not say which.
					theses={theses}
					className="min-h-[320px] flex-1"
				/>
			)}
		</section>
	)
}

export default TvMarketChart
